import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Container,
  Divider,
  CardActionArea,
  Stack,
  Paper,
  Link,
  Grid2,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { TipsAndUpdates } from "@mui/icons-material";
import { ReactComponent as RequirementIcon } from "../../../images/RequirememtPhase.svg";
import { ReactComponent as DesignPhase } from "../../../images/DesignPhase.svg";
import { ReactComponent as SASTPhase } from "../../../images/SASTPhase.svg";
import { ReactComponent as DASTPhase } from "../../../images/DASTPhase.svg";
import { ReactComponent as InsightsPhase } from "../../../images/InsightsPhase.svg";
import { ReactComponent as SecurityTestPlan } from "../../../images/SecurityTestPlan.svg";

function OptionCard({ icon, title, description, onClick }) {
  return (
    <Card
      elevation={0}
      sx={{
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          transform: "translateY(-4px)",
        },
      }}
      onClick={onClick}
    >
      <CardActionArea
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          padding: 0,
        }}
      >
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
          }}
        >
          {/* Icon and Title - Fixed Space */}
          <Box
            sx={{
              width: "100%",
              mb: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="center"
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  fontSize: {
                    xs: "1.4rem",
                    sm: "1.6rem",
                    md: "1.8rem",
                  },
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                    md: "1.1rem",
                  },
                  fontWeight: 600,
                  textAlign: "left",
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
            </Stack>
          </Box>

          {/* Description - Flexible Space */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "flex-start",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.8rem",
                  md: "0.85rem",
                },
                lineHeight: 1.4,
                textAlign: "center",
                width: "100%",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: {
                  xs: 8, // Increased line limits
                  sm: 10,
                  md: 12,
                },
                WebkitBoxOrient: "vertical",
                wordWrap: "break-word",
              }}
            >
              {description}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const SecurityTestingHomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleNavigate = (index) => () => {
    if (index === 0) {
      navigate("/dashboard/security-testing/input");
    } else if (index === 1) {
      navigate("/dashboard/security-testing/design-phase-output");
    } else if (index === 2) {
      navigate("/dashboard/security-testing/construction-phase");
    } else if (index === 3) {
      navigate("/dashboard/security-testing/dast-phase");
    } else if (index === 4) {
      navigate("/dashboard/security-testing/insights-analysis-page");
    } else if (index === 5) {
      navigate("/dashboard/security-testing/security-test-plan-home-page");
    }
  };

  const phases = [
    {
      id: "requirement",
      title: "Requirement Phase",
      description:
        "Generate Functional modules, Security Requirements, System Architecture",
      icon: <RequirementIcon />,
      bordered: true,
    },
    {
      id: "design",
      title: "Design Phase",
      description:
        "Generate Security Recommendations, Threat Modelling, Architecture Design Review",
      icon: <DesignPhase />,
      bordered: false,
    },
    {
      id: "sast",
      title: "SAST Phase",
      description:
        "Generate detailed reports, Visual graphs, and AI-driven insights.",
      icon: <SASTPhase />,
      bordered: false,
    },
    {
      id: "dast",
      title: "DAST Phase",
      description:
        "Directly automate existing or user-defined API test cases without going through test design or scenario generation steps.",
      icon: <DASTPhase />,
      bordered: false,
    },
    {
      id: "insights",
      title: "Insights Phase",
      description:
        "Generate comprehensive insights and analytics from security testing results.",
      icon: <InsightsPhase />,
      bordered: false,
    },
    {
      id: "testPlan",
      title: "Security Test Plan",
      description:
        "Execute dynamic security testing in live or staging environments, generating comprehensive test plans.",
      icon: <SecurityTestPlan />,
      bordered: false,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: {
          xs: 4,
          sm: 5,
          md: 6,
        },
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
      }}
    >
      {/* Title */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontSize: {
            xs: "1.8rem",
            sm: "2.125rem",
            md: "2.5rem",
          },
        }}
      >
        QMentisAI: AI-Driven Application Security Across the SDLC
      </Typography>
      <Box>
        <Typography
          variant="subtitle1"
          color="text"
          textAlign="center"
          sx={{
            maxWidth: {
              xs: "95%",
              sm: "90%",
              md: "85%",
              lg: "80%",
            },
            mx: "auto",
            fontSize: {
              xs: "0.9rem",
              sm: "1rem",
              md: "1.1rem",
            },
          }}
        >
          QMentisAI delivers intelligent, end-to-end application security across
          the software development lifecycle — seamlessly integrating security
          from initial planning through to deployment. It automates architecture
          generation, conducts comprehensive SAST and DAST assessments, and
          provides actionable insights through a unified, analytics-driven
          dashboard.
        </Typography>
      </Box>

      <Divider
        sx={{
          my: {
            xs: 3,
            sm: 4,
            md: 5,
          },
          width: "100%",
          maxWidth: {
            xs: "100%",
            sm: "800px",
            md: "900px",
          },
          borderBottomWidth: 2,
        }}
      />

      {/* Phases Grid - Using proper Grid2 */}
      <Container
        maxWidth="lg"
        sx={{
          px: {
            xs: 1,
            sm: 2,
            md: 3,
          },
        }}
      >
        <Grid2
          container
          spacing={{
            xs: 2,
            sm: 3,
            md: 4,
          }}
          sx={{
            justifyContent: "center",
            alignItems: "stretch", // This ensures equal heights
          }}
        >
          {phases.map((phase, index) => (
            <Grid2
              key={phase.id}
              size={{ xs: 12, sm: 6 }}
              sx={{
                display: "flex",
                minHeight: 0, // Allows flex children to shrink
              }}
            >
              <OptionCard
                icon={phase.icon}
                title={phase.title}
                description={phase.description}
                onClick={handleNavigate(index)}
              />
            </Grid2>
          ))}
        </Grid2>
      </Container>

      {/* Tip Section */}
      <Paper
        elevation={0}
        sx={{
          mt: {
            xs: 6,
            sm: 7,
            md: 8,
          },
          bgcolor: "#fffbea",
          border: "1px solid #ffe58f",
          borderRadius: 2,
          p: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },
          mx: 2,
          maxWidth: {
            xs: "100%",
            sm: "90%",
            md: "800px",
            lg: "900px",
          },
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <TipsAndUpdates sx={{ color: "#faad14", flexShrink: 0 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: {
              xs: "0.8rem",
              sm: "0.875rem",
            },
          }}
        >
          <b>Here is a tip for you:</b> Most teams start with the Requirement
          Phase for comprehensive security testing. You can navigate between
          phases as needed.
        </Typography>
      </Paper>

      {/* Help Link */}
      <Link
        // href="#"
        underline="hover"
        sx={{
          mt: 3,
          fontSize: {
            xs: "0.8rem",
            sm: "0.9rem",
          },
          color: "primary.main",
        }}
      >
        Need Help?
      </Link>
    </Box>
  );
};

export default SecurityTestingHomePage;
